/**
 * Per-employee calibration profile (§21).
 *
 * Why this matters more than any threshold default: the raw yaw of "looking at my main
 * monitor" is not zero for most people. A laptop camera sits below and often to one side of
 * the screen the person actually works on; an external monitor may be 20° off the camera
 * axis. Without calibration, an employee whose monitor sits to their right registers a
 * permanent rightward yaw and accrues attention-away events for doing nothing but their job.
 *
 * Calibration therefore records two things:
 *
 * 1. A **neutral pose** — the employee's actual "looking at my work screen" orientation,
 *    which every subsequent angle is measured relative to.
 * 2. **Per-side thresholds** derived from the employee's own comfortable range, rather than
 *    a single global cone, because real workstations are asymmetric.
 *
 * Each derived threshold is clamped into the configured band, so a mis-performed calibration
 * cannot produce an absurdly narrow cone that flags everything or a wide one that flags
 * nothing.
 */

import type { CalibrationPolicy, ThresholdPolicy } from '../config.js';
import { CalibrationError } from '../errors.js';
import { headPose, type HeadPose } from '../models.js';
import type { EffectiveThresholds } from '../attention/classifier.js';

export const PROFILE_VERSION = 1;

export const CALIBRATION_STEPS = ['forward', 'left', 'right', 'up', 'down'] as const;
export type CalibrationStepName = (typeof CALIBRATION_STEPS)[number];

/** Only the neutral pose is strictly required; the rest fall back to policy. */
export const REQUIRED_STEPS: readonly CalibrationStepName[] = ['forward'];

export interface CalibrationProfile {
  readonly version: number;
  readonly createdAt: Date;
  readonly neutral: HeadPose;
  readonly yawForwardMaxLeft: number;
  readonly yawForwardMaxRight: number;
  readonly pitchUpMax: number;
  readonly pitchDownMax: number;
  readonly observed: Readonly<Partial<Record<CalibrationStepName, HeadPose>>>;
  readonly sampleCounts: Readonly<Partial<Record<CalibrationStepName, number>>>;
  readonly notes: readonly string[];
  readonly agentVersion: string;
  readonly modelVersion: string;
}

export function profileAgeDays(profile: CalibrationProfile, now: Date = new Date()): number {
  return Math.max(0, (now.getTime() - profile.createdAt.getTime()) / 86_400_000);
}

export function isProfileStale(
  profile: CalibrationProfile,
  maxAgeDays: number,
  now: Date = new Date(),
): boolean {
  return profileAgeDays(profile, now) > maxAgeDays;
}

export function profileStalenessNote(
  profile: CalibrationProfile,
  maxAgeDays: number,
  now: Date = new Date(),
): string | null {
  if (!isProfileStale(profile, maxAgeDays, now)) return null;
  return (
    `calibration profile is ${profileAgeDays(profile, now).toFixed(0)} days old (limit ` +
    `${maxAgeDays}); seating and camera position drift over time, so thresholds may no ` +
    'longer match this workstation'
  );
}

/**
 * Combine calibrated cones with the organisation's strong-away and roll limits.
 *
 * Strong-away limits stay under organisational control but are never allowed to sit inside
 * the calibrated forward cone — an incoherent combination that would report someone as
 * strongly away while they look straight at their monitor.
 */
export function effectiveThresholdsFor(
  profile: CalibrationProfile,
  policy: ThresholdPolicy,
): EffectiveThresholds {
  const widestYaw = Math.max(profile.yawForwardMaxLeft, profile.yawForwardMaxRight);
  const widestPitch = Math.max(profile.pitchUpMax, profile.pitchDownMax);
  const narrowestYaw = Math.min(profile.yawForwardMaxLeft, profile.yawForwardMaxRight);

  return {
    yawForwardMaxLeft: profile.yawForwardMaxLeft,
    yawForwardMaxRight: profile.yawForwardMaxRight,
    yawStrongAway: Math.max(policy.yawStrongAway, widestYaw * 1.8),
    pitchUpMax: profile.pitchUpMax,
    pitchDownMax: profile.pitchDownMax,
    pitchStrongAway: Math.max(policy.pitchStrongAway, widestPitch * 1.8),
    rollTolerance: policy.rollTolerance,
    // Hysteresis must stay well inside the narrower cone or returning to forward becomes
    // impossible on that side.
    hysteresisDegrees: Math.min(policy.hysteresisDegrees, Math.max(1, narrowestYaw * 0.5)),
    neutral: profile.neutral,
    source: 'calibrated',
  };
}

export interface DeriveProfileInput {
  stepPoses: Partial<Record<CalibrationStepName, HeadPose>>;
  policy: CalibrationPolicy;
  thresholds: ThresholdPolicy;
  sampleCounts?: Partial<Record<CalibrationStepName, number>>;
  createdAt?: Date;
  agentVersion?: string;
  modelVersion?: string;
}

/**
 * Derive thresholds from the poses measured during a calibration session.
 *
 * A threshold sits `thresholdFraction` of the way from the neutral pose to the employee's
 * own comfortable extreme. At the default 0.55, someone who can comfortably turn 40° from
 * their monitor gets a 22° forward cone: wide enough that normal shifts of posture do not
 * trip it, narrow enough that a genuine turn away does.
 *
 * Any axis the employee did not complete falls back to the organisation's policy value, and
 * the fallback is recorded in `notes`, so a half-finished calibration is visible rather than
 * silently degrading accuracy.
 */
export function deriveProfile(input: DeriveProfileInput): CalibrationProfile {
  const { stepPoses, policy, thresholds } = input;
  const neutral = stepPoses.forward;
  if (neutral === undefined) {
    throw new CalibrationError(
      'Calibration requires the forward step: without a neutral pose there is nothing to ' +
        'measure other angles against.',
    );
  }

  const notes: string[] = [];

  const derive = (
    step: CalibrationStepName,
    axis: 'yaw' | 'pitch',
    floor: number,
    ceiling: number,
    policyValue: number,
  ): number => {
    const observed = stepPoses[step];
    if (observed === undefined) {
      notes.push(
        `'${step}' step was not completed; using the policy value ${policyValue.toFixed(0)}deg ` +
          'for that direction',
      );
      return clamp(policyValue, floor, ceiling);
    }

    const extreme = Math.abs(observed[axis] - neutral[axis]);
    if (extreme < floor) {
      notes.push(
        `'${step}' step moved only ${extreme.toFixed(1)}deg from neutral, below the ` +
          `${floor.toFixed(0)}deg minimum; using the minimum. Consider recalibrating with a ` +
          'fuller head movement.',
      );
      return floor;
    }
    const derived = extreme * policy.thresholdFraction;
    const clamped = clamp(derived, floor, ceiling);
    if (clamped !== derived) {
      notes.push(
        `'${step}' derived threshold ${derived.toFixed(1)}deg was clamped to ` +
          `${clamped.toFixed(1)}deg by the configured limits`,
      );
    }
    return clamped;
  };

  const yawLeft = derive(
    'left',
    'yaw',
    policy.minYawThreshold,
    policy.maxYawThreshold,
    thresholds.yawForwardMax,
  );
  const yawRight = derive(
    'right',
    'yaw',
    policy.minYawThreshold,
    policy.maxYawThreshold,
    thresholds.yawForwardMax,
  );
  const pitchUp = derive(
    'up',
    'pitch',
    policy.minPitchThreshold,
    policy.maxPitchThreshold,
    thresholds.pitchUpMax,
  );
  const pitchDown = derive(
    'down',
    'pitch',
    policy.minPitchThreshold,
    policy.maxPitchThreshold,
    thresholds.pitchDownMax,
  );

  noteDirectionSanity(stepPoses, neutral, notes);

  return {
    version: PROFILE_VERSION,
    createdAt: input.createdAt ?? new Date(),
    neutral,
    yawForwardMaxLeft: yawLeft,
    yawForwardMaxRight: yawRight,
    pitchUpMax: pitchUp,
    pitchDownMax: pitchDown,
    observed: { ...stepPoses },
    sampleCounts: { ...(input.sampleCounts ?? {}) },
    notes,
    agentVersion: input.agentVersion ?? '',
    modelVersion: input.modelVersion ?? '',
  };
}

/**
 * Flag a calibration whose steps went the wrong way.
 *
 * If the "left" step produced a rightward yaw, something is inverted — a mirrored camera
 * feed, or the employee following the prompt from the screen's point of view instead of their
 * own. That must be surfaced loudly: a mirrored calibration silently reverses every direction
 * statistic the system will ever produce for this person.
 */
function noteDirectionSanity(
  stepPoses: Partial<Record<CalibrationStepName, HeadPose>>,
  neutral: HeadPose,
  notes: string[],
): void {
  const expectations = [
    ['left', 'yaw', -1, "the employee's left"],
    ['right', 'yaw', 1, "the employee's right"],
    ['up', 'pitch', 1, 'upward'],
    ['down', 'pitch', -1, 'downward'],
  ] as const;

  for (const [step, axis, expectedSign, description] of expectations) {
    const observed = stepPoses[step];
    if (observed === undefined) continue;
    const delta = observed[axis] - neutral[axis];
    if (Math.abs(delta) < 3) continue;
    if (delta > 0 !== expectedSign > 0) {
      notes.push(
        `WARNING: the '${step}' step moved ${Math.abs(delta).toFixed(1)}deg in the opposite ` +
          `direction to ${description}. The camera feed may be mirrored, or the prompts may ` +
          'have been followed from the screen\'s point of view. Direction statistics will be ' +
          'reversed until this is corrected — recalibrate before relying on them.',
      );
    }
  }
}

// ---------------------------------------------------------------------------------------
// Serialisation
// ---------------------------------------------------------------------------------------

export function profileToJson(profile: CalibrationProfile): Record<string, unknown> {
  return {
    version: profile.version,
    created_at: profile.createdAt.toISOString(),
    neutral: poseToJson(profile.neutral),
    thresholds: {
      yaw_forward_max_left: round(profile.yawForwardMaxLeft, 2),
      yaw_forward_max_right: round(profile.yawForwardMaxRight, 2),
      pitch_up_max: round(profile.pitchUpMax, 2),
      pitch_down_max: round(profile.pitchDownMax, 2),
    },
    observed: Object.fromEntries(
      Object.entries(profile.observed).map(([name, pose]) => [name, poseToJson(pose as HeadPose)]),
    ),
    sample_counts: { ...profile.sampleCounts },
    notes: [...profile.notes],
    agent_version: profile.agentVersion,
    model_version: profile.modelVersion,
  };
}

export function profileFromJson(data: unknown): CalibrationProfile {
  if (typeof data !== 'object' || data === null) {
    throw new CalibrationError('Calibration profile is not an object');
  }
  const record = data as Record<string, unknown>;
  const version = Number(record.version ?? PROFILE_VERSION);
  if (version !== PROFILE_VERSION) {
    throw new CalibrationError(
      `Calibration profile version ${version} is not supported by this agent (expected ` +
        `${PROFILE_VERSION}). Re-run calibration.`,
    );
  }

  const thresholds = record.thresholds as Record<string, unknown> | undefined;
  if (thresholds === undefined) {
    throw new CalibrationError('Calibration profile is missing its thresholds');
  }

  const createdAt = new Date(String(record.created_at));
  if (Number.isNaN(createdAt.getTime())) {
    throw new CalibrationError('Calibration profile has an unreadable created_at');
  }

  const observedRaw = (record.observed ?? {}) as Record<string, unknown>;
  const observed: Partial<Record<CalibrationStepName, HeadPose>> = {};
  for (const step of CALIBRATION_STEPS) {
    const pose = observedRaw[step];
    if (pose !== undefined) observed[step] = poseFromJson(pose);
  }

  return {
    version,
    createdAt,
    neutral: poseFromJson(record.neutral),
    yawForwardMaxLeft: requireNumber(thresholds.yaw_forward_max_left, 'yaw_forward_max_left'),
    yawForwardMaxRight: requireNumber(thresholds.yaw_forward_max_right, 'yaw_forward_max_right'),
    pitchUpMax: requireNumber(thresholds.pitch_up_max, 'pitch_up_max'),
    pitchDownMax: requireNumber(thresholds.pitch_down_max, 'pitch_down_max'),
    observed,
    sampleCounts: (record.sample_counts ?? {}) as Partial<Record<CalibrationStepName, number>>,
    notes: Array.isArray(record.notes) ? record.notes.map(String) : [],
    agentVersion: String(record.agent_version ?? ''),
    modelVersion: String(record.model_version ?? ''),
  };
}

export function describeProfile(profile: CalibrationProfile, now: Date = new Date()): string {
  const lines = [
    `Calibration profile (created ${profile.createdAt.toISOString()}, ` +
      `${profileAgeDays(profile, now).toFixed(1)} days old)`,
    `  neutral pose  yaw ${fmt(profile.neutral.yaw)}  pitch ${fmt(profile.neutral.pitch)}  ` +
      `roll ${fmt(profile.neutral.roll)}`,
    `  forward cone  left ${profile.yawForwardMaxLeft.toFixed(1)}deg  ` +
      `right ${profile.yawForwardMaxRight.toFixed(1)}deg  ` +
      `up ${profile.pitchUpMax.toFixed(1)}deg  down ${profile.pitchDownMax.toFixed(1)}deg`,
  ];
  for (const step of CALIBRATION_STEPS) {
    const pose = profile.observed[step];
    if (pose === undefined) continue;
    lines.push(
      `  observed ${step.padEnd(8)} yaw ${fmt(pose.yaw)}  pitch ${fmt(pose.pitch)}  ` +
        `(${profile.sampleCounts[step] ?? 0} samples)`,
    );
  }
  for (const note of profile.notes) lines.push(`  note: ${note}`);
  return lines.join('\n');
}

function poseToJson(pose: HeadPose): Record<string, number> {
  return { yaw: round(pose.yaw, 2), pitch: round(pose.pitch, 2), roll: round(pose.roll, 2) };
}

function poseFromJson(value: unknown): HeadPose {
  if (typeof value !== 'object' || value === null) {
    throw new CalibrationError('Calibration profile contains a malformed pose');
  }
  const record = value as Record<string, unknown>;
  return headPose(
    requireNumber(record.yaw, 'yaw'),
    requireNumber(record.pitch, 'pitch'),
    requireNumber(record.roll, 'roll'),
  );
}

function requireNumber(value: unknown, name: string): number {
  const parsed = typeof value === 'string' ? Number(value) : value;
  if (typeof parsed !== 'number' || !Number.isFinite(parsed)) {
    throw new CalibrationError(`Calibration profile field '${name}' is not a finite number`);
  }
  return parsed;
}

function clamp(value: number, low: number, high: number): number {
  return Math.max(low, Math.min(high, value));
}

function round(value: number, places: number): number {
  const factor = 10 ** places;
  return Math.round(value * factor) / factor;
}

function fmt(value: number): string {
  return `${value >= 0 ? '+' : ''}${value.toFixed(1)}deg`;
}
